import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faUsers,
  faPlus,
  faTrash,
  faSave,
  faTimes,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';
import projectService from '../../services/projectService';
import teamService from '../../services/teamService';
import { toast } from 'react-toastify';
import TenantSelector from '../common/TenantSelector';

const EditProjectForm = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'planning',
    team: [],
    assignedTeams: [],
    tenant: null
  });

  // Team assignment form
  const [showAssignTeamForm, setShowAssignTeamForm] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [project, teams] = await Promise.all([
        projectService.getProject(projectId),
        teamService.getTeams()
      ]);

      setFormData({
        name: project.name || '',
        client: project.client || '',
        description: project.description || '',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        status: project.status || 'planning',
        team: project.team || [],
        assignedTeams: project.assignedTeams || [],
        tenant: project.tenant?._id || project.tenant || null
      });
      setAvailableTeams(Array.isArray(teams) ? teams : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load project details');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Assigned teams handlers
  const handleAssignTeam = () => {
    if (selectedTeamId) {
      // Check if team is already assigned
      const alreadyAssigned = formData.assignedTeams.some(
        at => (at.team?._id || at.team) === selectedTeamId
      );

      if (alreadyAssigned) {
        toast.warning('This team is already assigned to the project');
        return;
      }

      const team = availableTeams.find(t => t._id === selectedTeamId);
      // Get role from team's type field (red, blue, purple -> red_team, blue_team, purple_team)
      const teamRole = team?.type ? `${team.type}_team` : 'red_team';

      setFormData(prev => ({
        ...prev,
        assignedTeams: [...prev.assignedTeams, {
          team: { _id: selectedTeamId, name: team?.name },
          role: teamRole,
          addedAt: new Date()
        }]
      }));
      setSelectedTeamId('');
      setShowAssignTeamForm(false);
    }
  };

  const handleRemoveAssignedTeam = (index) => {
    setFormData(prev => ({
      ...prev,
      assignedTeams: prev.assignedTeams.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Prepare update data - convert assignedTeams to proper format
      const updateData = {
        ...formData,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        assignedTeams: formData.assignedTeams.map(at => ({
          team: at.team?._id || at.team,
          role: at.role
        }))
      };

      await projectService.updateProject(projectId, updateData);
      toast.success('Project updated successfully!');
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error('Error updating project:', error);
      setError(error.response?.data?.message || 'Failed to update project. Please try again.');
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}`);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'red_team': return 'bg-red-100 text-red-800';
      case 'blue_team': return 'bg-blue-100 text-blue-800';
      case 'purple_team': return 'bg-purple-100 text-purple-800';
      case 'observer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'red_team': return 'Red Team';
      case 'blue_team': return 'Blue Team';
      case 'purple_team': return 'Purple Team';
      case 'observer': return 'Observer';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Project</h1>

      <div className="bg-white rounded-lg shadow-md p-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Project Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="client">
              Client Name *
            </label>
            <input
              type="text"
              id="client"
              name="client"
              value={formData.client}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={saving}
            ></textarea>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="startDate">
                Start Date
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="endDate">
                End Date
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="status">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={saving}
              >
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <TenantSelector
              value={formData.tenant}
              onChange={(val) => setFormData(prev => ({ ...prev, tenant: val }))}
              disabled={saving}
            />
          </div>

          {/* Assigned Teams Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-gray-700 text-sm font-bold flex items-center">
                <FontAwesomeIcon icon={faUsers} className="mr-2 text-purple-600" />
                Assigned Teams
              </label>
              <button
                type="button"
                onClick={() => setShowAssignTeamForm(true)}
                className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 flex items-center"
                disabled={saving}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Assign Team
              </button>
            </div>

            {formData.assignedTeams.length > 0 ? (
              <div className="space-y-2">
                {formData.assignedTeams.map((assignedTeam, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon icon={faUserShield} className="text-gray-500" />
                      <span className="font-medium">{assignedTeam.team?.name || 'Unknown Team'}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(assignedTeam.role)}`}>
                        {getRoleLabel(assignedTeam.role)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAssignedTeam(index)}
                      className="text-red-600 hover:text-red-800 flex items-center"
                      disabled={saving}
                    >
                      <FontAwesomeIcon icon={faTrash} className="mr-1" />
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No teams assigned yet. Assign teams to give their members access to this project.</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors flex items-center"
              disabled={saving}
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center"
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Assign Team Modal */}
      {showAssignTeamForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <FontAwesomeIcon icon={faUsers} className="mr-2 text-purple-600" />
              Assign Team to Project
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Team *</label>
                <select
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">-- Select a team --</option>
                  {availableTeams.map(team => (
                    <option key={team._id} value={team._id}>
                      {team.name} ({team.type ? `${team.type.charAt(0).toUpperCase() + team.type.slice(1)} Team` : 'Unknown'}) - {team.members?.length || 0} members
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The team's role is determined by its type set during team creation
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowAssignTeamForm(false);
                  setSelectedTeamId('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 flex items-center"
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Cancel
              </button>
              <button
                onClick={handleAssignTeam}
                disabled={!selectedTeamId}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-purple-400 flex items-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Assign Team
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EditProjectForm;
